import { useId } from 'react';
import { Link } from 'react-router-dom';
import {
  NO_OVERRIDES, companyDimension, companyNetPay, companyWageMode, effectiveDimension, effectiveNetPay, effectiveWageMode, netPayForcedConsolidated,
  type OptionOverrides, type SettingsState,
} from '../exportOptionState';
import {
  DIMENSION_HELP, DIMENSION_LABEL, NET_PAY_CHOICE_LABEL, NET_PAY_COPY, NET_PAY_SHORT_LABEL, WAGE_MODE_COPY, WAGE_MODE_LABEL,
} from '../exportOptions';
import { NET_PAY_DETAIL_MODES, SELECTABLE_WAGE_MODES, WAGE_GROUP_DIMENSIONS, type AccountingExportCapabilities, type ExportMode, type WageGroupDimension, type WageMappingMode } from '../exportTypes';

const WAGE_SHORT: Record<Exclude<WageMappingMode, 'LegacyResolver'>, string> = {
  SingleAccount: 'All wages post to the default Wage Expense account.',
  GroupBased: "Each group's wages post to the account mapped to that group.",
};

/**
 * The two journal options on the Payroll Export tab. They start from the COMPANY setting ("Using company setting"); a person who may generate or manage mappings can
 * change either for this preview and export only ("Changed for this preview"). Only a changed choice is sent, so nothing changes unless someone chooses it.
 * The net pay choice exists only for the compound entry (the per-employee mode already keeps every line per employee).
 */
export function ExportOptionsPanel({ mode, settings, caps, overrides, onChange, perEmployeeAllowed }: {
  mode: ExportMode;
  settings: SettingsState;
  caps: AccountingExportCapabilities | null;
  overrides: OptionOverrides;
  onChange: (next: OptionOverrides) => void;
  perEmployeeAllowed: boolean;
}) {
  const uid = useId();
  const canOverride = caps === null || caps.canManageMappings || caps.canGenerate;
  const canManage = caps === null || caps.canManageMappings;
  const netPay = effectiveNetPay(overrides, settings);
  const wageMode = effectiveWageMode(overrides, settings);
  const dimension = effectiveDimension(overrides, settings);
  const forced = netPayForcedConsolidated(overrides, settings, perEmployeeAllowed);
  const netPayChanged = overrides.netPay !== null && overrides.netPay !== companyNetPay(settings);
  const wageChanged = (overrides.wageMode !== null && overrides.wageMode !== companyWageMode(settings))
    || (overrides.dimension !== null && overrides.dimension !== companyDimension(settings));

  const company = settings.kind === 'ready' ? settings.settings : null;
  const companyLine = settings.kind === 'loading'
    ? 'Checking the company setting…'
    : settings.kind === 'unavailable'
      ? 'The company setting could not be read here. The server applies it when you preview.'
      : company?.isConfigured
        ? `Company setting: ${NET_PAY_SHORT_LABEL[company.netPayDetailMode]} net pay, wages ${WAGE_MODE_LABEL[company.wageMappingMode].toLowerCase()}${company.wageGroupDimension ? ` (${DIMENSION_LABEL[company.wageGroupDimension]})` : ''}.`
        : 'No company setting is saved, so the original rules apply. Choose an option below to try a different one for this preview.';

  const set = (patch: Partial<OptionOverrides>) => onChange({ ...overrides, ...patch });
  const reset = (which: 'netPay' | 'wage') => onChange(which === 'netPay' ? { ...overrides, netPay: null } : { ...overrides, wageMode: null, dimension: null });

  const netPayId = `${uid}-netpay`;
  const wageId = `${uid}-wage`;

  return (
    <section className="ax-options" aria-labelledby={`${uid}-title`} data-testid="export-options">
      <h4 id={`${uid}-title`}>Journal options</h4>
      <p className="jr-hint" data-testid="options-company-line">
        {companyLine}{' '}
        {canManage && <Link to="/accounting?tab=export-settings">Change the company setting</Link>}
      </p>
      {!canOverride && (
        <p className="jr-readonly" role="note" data-testid="options-readonly">
          These options are shown for information. You can preview journal entries, but choosing different options needs a permission you do not have.
        </p>
      )}

      {mode === 'Compound' ? (
        <fieldset className="jr-choice ax-opt-group" data-testid="netpay-detail" disabled={!canOverride}>
          <legend>Net pay detail</legend>
          {NET_PAY_DETAIL_MODES.map((value) => {
            const blocked = value === 'PerEmployee' && !perEmployeeAllowed;
            return (
              <div key={value} className="ax-opt">
                <label>
                  <input
                    type="radio" name={netPayId} value={value} checked={netPay === value && !(forced && value === 'PerEmployee')} disabled={blocked || !canOverride}
                    data-testid={`netpay-mode-${value}`} aria-describedby={`${netPayId}-${value}-copy${blocked ? ` ${netPayId}-denied` : ''}`}
                    onChange={() => set({ netPay: value })}
                  />
                  <span>{NET_PAY_CHOICE_LABEL[value]}</span>
                </label>
                <p id={`${netPayId}-${value}-copy`} className="jr-hint ax-opt__copy" data-testid={`netpay-copy-${value}`}>{NET_PAY_COPY[value]}</p>
              </div>
            );
          })}
          {!perEmployeeAllowed && (
            <p id={`${netPayId}-denied`} className="jr-hint" data-testid="netpay-denied">
              One net pay line for each employee is unavailable: it needs permission to view employee compensation. Ask a company administrator.
            </p>
          )}
          {forced && (
            <p className="jr-warning" role="note" data-testid="netpay-forced">
              Your company setting is one net pay line for each employee, which you cannot use without permission to view employee compensation. This preview and export use the consolidated line instead.
            </p>
          )}
          <p className="jr-hint" data-testid="netpay-source">
            {netPayChanged ? 'Changed for this preview.' : netPay ? 'Using company setting.' : 'Using the original rules: one summarized net pay line for each mapped account.'}
            {netPayChanged && canOverride && <> <button type="button" className="ax-link-btn" onClick={() => reset('netPay')}>Use company setting</button></>}
          </p>
        </fieldset>
      ) : (
        <p className="jr-hint" data-testid="netpay-fixed">Net pay detail: in the per-employee mode every line, net pay included, is kept per employee, so there is nothing to choose.</p>
      )}

      <fieldset className="jr-choice ax-opt-group" data-testid="wage-mapping" disabled={!canOverride}>
        <legend>Wage expense mapping</legend>
        {SELECTABLE_WAGE_MODES.map((value) => (
          <div key={value} className="ax-opt">
            <label>
              <input
                type="radio" name={wageId} value={value} checked={wageMode === value} disabled={!canOverride} data-testid={`wage-mode-${value}`}
                aria-describedby={`${wageId}-${value}-copy`}
                onChange={() => set({ wageMode: value, dimension: value === 'GroupBased' ? (overrides.dimension ?? companyDimension(settings) ?? 'Department') : null })}
              />
              <span>{WAGE_MODE_LABEL[value]}</span>
            </label>
            <p id={`${wageId}-${value}-copy`} className="jr-hint ax-opt__copy">{WAGE_SHORT[value]}</p>
          </div>
        ))}
        {wageMode === 'GroupBased' && (
          <div className="jr-field ax-opt__dimension">
            <label htmlFor={`${wageId}-dimension`}>Group by</label>
            <select
              id={`${wageId}-dimension`} value={dimension ?? 'Department'} disabled={!canOverride} data-testid="wage-dimension" aria-describedby={`${wageId}-dimension-help`}
              onChange={(e) => set({ wageMode: 'GroupBased', dimension: e.target.value as WageGroupDimension })}
            >
              {WAGE_GROUP_DIMENSIONS.map((d) => <option key={d} value={d}>{DIMENSION_LABEL[d]}</option>)}
            </select>
            <p id={`${wageId}-dimension-help`} className="jr-hint">{dimension ? DIMENSION_HELP[dimension] : ''}</p>
          </div>
        )}
        <div className="ax-explain" data-testid="wage-explanation" role="note">
          <p><strong>{wageMode ? WAGE_MODE_LABEL[wageMode] : 'Original rules'}:</strong> {wageMode ? WAGE_MODE_COPY[wageMode] : WAGE_MODE_COPY.LegacyResolver}</p>
        </div>
        <p className="jr-hint" data-testid="wage-source">
          {wageChanged ? 'Changed for this preview.' : wageMode ? 'Using company setting.' : 'Using the original rules.'}
          {wageChanged && canOverride && <> <button type="button" className="ax-link-btn" onClick={() => reset('wage')}>Use company setting</button></>}
        </p>
      </fieldset>
      {(netPayChanged || wageChanged) && (
        <p className="jr-hint">
          A change made here applies to this preview and export only. It is recorded with the export. To change the default for everyone, use the company setting.{' '}
          <button type="button" className="ax-link-btn" onClick={() => onChange(NO_OVERRIDES)}>Reset both</button>
        </p>
      )}
    </section>
  );
}
